import { Link } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface LuxuryCardProps {
  title: string;
  image: string;
  href: string;
  description?: string;
  price?: string;
  features?: string[];
  delay?: number;
}

export default function LuxuryCard({

  title,
  image,
  href,
  description,
  price,
  features,
  delay = 0,
}: LuxuryCardProps) {
  const { t } = useTranslation();
  const isExternal = href.startsWith("http://") || href.startsWith("https://");
  
  const itemSlug = href.split('/').pop();
  const isRoom = href.includes('/accommodation');
  const isBlog = href.includes('/news');
  const translatedTitle = isRoom ? t(`rooms.${itemSlug}.title`, { defaultValue: title }) : isBlog ? t(`blog.${itemSlug}.title`, { defaultValue: title }) : title;
  const translatedDesc = description ? (isRoom ? t(`rooms.${itemSlug}.description`, { defaultValue: description }) : isBlog ? t(`blog.${itemSlug}.excerpt`, { defaultValue: description }) : description) : undefined;

  const renderLink = (children: React.ReactNode, className?: string) => {
    if (isExternal) {
      return (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer" 
          className={className}
        >
          {children}
        </a>
      );
    }
    return (
      <LocalLink to={href} className={className}>
        {children}
      </LocalLink>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col bg-secondary rounded-2xl overflow-hidden shadow-2xl hover:shadow-gold/10 transition-shadow duration-500"
    >
      {renderLink(
        <>
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors duration-500 z-10" />
          <img
            src={image}
            alt={translatedTitle}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
          />
        </>,
        "relative h-80 w-full overflow-hidden block"
      )}
      
      <div className="p-8 flex flex-col flex-1">
        <div className="flex justify-between items-start mb-4 gap-4">
          {renderLink(
            <h3 className="text-2xl font-bold group-hover:text-gold transition-colors">
              {translatedTitle}
            </h3>
          )}
          {price && <span className="text-gold font-semibold shrink-0 text-right">{price}</span>}
        </div>
        
        {translatedDesc && (
          <p className="text-white/70 text-sm mb-6 line-clamp-2 leading-relaxed">
            {translatedDesc}
          </p>
        )}
        
        {features && (
          <div className="flex flex-wrap gap-2 mb-6 mt-auto">
            {features.map((feature, i) => (
              <span
                key={i}
                className="text-xs px-3 py-1 bg-white/5 rounded-full text-white/80 border border-white/10"
              >
                {isRoom ? t(`rooms.features.${feature}`, { defaultValue: feature }) : isBlog ? t(`blog.tags.${feature}`, { defaultValue: feature }) : feature}
              </span>
            ))}
          </div>
        )}
        
        {renderLink(
          <>
            {t('luxuryCard.discover', { defaultValue: 'Discover' })}
            <motion.span
              className="inline-block"
              initial={{ x: 0 }}
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              →
            </motion.span>
          </>,
          "mt-auto uppercase tracking-widest text-sm font-semibold text-gold group-hover:text-white transition-colors flex items-center gap-2 w-fit"
        )}
      </div>
    </motion.div>
  );
}
